import type React from 'react';

import {
	SPACE_OVERVIEW,
	VIEW_BLOG,
	VIEW_PAGE,
	WHITEBOARD_BOARD,
	SLIDES_VIEW,
	DATABASE_VIEW,
	EMBED_VIEW,
} from '@confluence/named-routes/entry-points/routes.ts';
import type { generateSlug } from '@confluence/route-manager-utils/entry-points/url.ts';
import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import type { BreadcrumbsDropdownContentQuery$data } from './__generated__/BreadcrumbsDropdownContentQuery.graphql.ts';

type ModernizedBreadcrumbsQueryContentType = NonNullable<
	NonNullable<NonNullable<BreadcrumbsDropdownContentQuery$data['content']>['nodes']>[0]
>;
type ModernizedBreadcrumbsQueryAncestorType = NonNullable<
	NonNullable<ModernizedBreadcrumbsQueryContentType['ancestors']>[0]
>;
type ModernizedBreadcrumbsQueryPropertiesType = NonNullable<
	ModernizedBreadcrumbsQueryAncestorType['properties']
>;

export const CONTENT_STATUS_ARCHIVED = 'archived';

type BuildContentPathType = {
	contentId?: string | null;
	contentSlug?: ReturnType<typeof generateSlug>;
	contentType: string | null;
	isSpaceOverview: boolean;
	query?: any;
	spaceAlias?: string | null;
	spaceKey: string;
};

export const buildContentPath = ({
	contentId,
	contentSlug,
	contentType,
	isSpaceOverview,
	query,
	spaceAlias,
	spaceKey,
}: BuildContentPathType) => {
	if (contentType === 'folder') {
		return '#';
	}

	let route: typeof VIEW_PAGE;
	if (isSpaceOverview) {
		route = SPACE_OVERVIEW;
	} else {
		switch (contentType) {
			case 'blogpost':
				route = VIEW_BLOG;
				break;
			case 'page':
				route = VIEW_PAGE;
				break;
			case 'whiteboard':
				route = WHITEBOARD_BOARD;
				break;
			case 'slide':
				route = SLIDES_VIEW;
				break;
			case 'database':
				route = DATABASE_VIEW;
				break;
			case 'embed':
				route = EMBED_VIEW;
				break;
			default:
				throw new Error(`Received an unexpected content type: '${contentType}'`);
		}
	}

	return route.toUrl({ contentId, contentSlug, spaceKey: spaceAlias || spaceKey }, { query });
};

export const filterAncestors = (
	{ ancestors, status }: ModernizedBreadcrumbsQueryContentType,
	spaceHomepageId: string | null | undefined,
): ModernizedBreadcrumbsQueryAncestorType[] => {
	if (!ancestors) {
		return [];
	}

	// If current page is archived, filter out non-archived ancestors
	if (status === CONTENT_STATUS_ARCHIVED) {
		ancestors = ancestors.filter(
			(ancestor) => ancestor && ancestor.status === CONTENT_STATUS_ARCHIVED,
		);
	} else {
		// technically array elements can be null
		ancestors = ancestors.filter((node) => node);
	}

	// Filter out ancestors with null type when experiment is enabled
	ancestors = ancestors.filter((node) => {
		if (!node) {
			return false;
		}
		if (!node.type && fg('confluence_ufo_trace_ct_err')) {
			return false;
		}
		return true;
	});

	// Filter out ancestors with null id to prevent TypeError from path-to-regexp
	if (expVal('confluence_fix_expected_contentid_to_be_string', 'isEnabled', false)) {
		ancestors = ancestors.filter((node) => node && node.id);
	}

	if (!ancestors.length) {
		return ancestors as ModernizedBreadcrumbsQueryAncestorType[];
	}

	if (
		ancestors[0]!.id !== spaceHomepageId &&
		ancestors[ancestors.length - 1]!.id === spaceHomepageId
	) {
		return [...ancestors].reverse().slice(1) as ModernizedBreadcrumbsQueryAncestorType[];
	}
	if (ancestors[0]!.id === spaceHomepageId) {
		return ancestors.slice(1) as ModernizedBreadcrumbsQueryAncestorType[];
	}
	return ancestors as ModernizedBreadcrumbsQueryAncestorType[];
};

export const getEmojiFromPublishedPageProperties = (
	properties: ModernizedBreadcrumbsQueryPropertiesType | null,
) => {
	if (!properties) {
		return undefined;
	}

	const values = properties?.nodes?.filter((node) => node?.key === 'emoji-title-published');
	return (values?.[0]?.value || '').replace(/['"]+/g, '').replace(/null/, '');
};

export const getEmojiFromDraftPageProperties = (
	properties: ModernizedBreadcrumbsQueryPropertiesType | null,
) => {
	if (!properties) {
		return undefined;
	}

	const values = properties?.nodes?.filter((node) => node?.key === 'emoji-title-draft');
	return (values?.[0]?.value || '').replace(/['"]+/g, '').replace(/null/, '');
};

export const getIsTextTruncated = (textRef: React.RefObject<HTMLElement>) => {
	if (textRef.current) {
		return textRef.current.scrollWidth > textRef.current.clientWidth;
	}
	return false;
};

export type BreadcrumbsDropdownItemType = {
	href: string;
	id: string;
	title: string;
	contentType: string;
	emoji: string | undefined;
	subType?: string | null;
};
